import { useEffect } from 'react';
import { TRANSCRIPTS_CATEGORIES, TRANSCRIPTS_CATEGORIES_LABELS, TRANSCRIPTS_CATEGORIES_SELECTION_LIMITS, TRANSCRIPTS_PAGE_SIZES } from '../../constants';
import { Transcript } from '../../interfaces/TranscriptsInterface';
import CustomButton from '../reusable/CustomButton';
import CustomTable, { TableHeaderData } from '../reusable/CustomTable';
import CustomTooltip from '../reusable/CustomTooltip';
import InfoIcon from '../assets/info-icon.svg';
import TranscriptSelectionTableRow from './TranscriptSelectionTableRow';
import { formatHelperTextCount, storeLastNavigatedPage } from './utilityFunctions';
import { useTheme } from '@mui/material/styles';
import { Skeleton, useMediaQuery } from '@mui/material';
import { useTranscriptSelectionOutletContext } from '../hooks/useTranscriptSelectionOutletContext';
import useSchools from '../hooks/useSchools';
import { useState } from 'react';
import useFetchData from '../hooks/useFetchData';
import { getTranscriptSelectionSummary } from '../../services/transcriptsService';
import CustomPagination from '../reusable/CustomPagination';
import useTranscriptSelectionPaginationData from '../hooks/useTranscriptSelectionPaginationData';
import useUserDetails from '../hooks/useUserDetails';
import { checkForLink } from './utilityFunctions';
import TableSkeleton from '../reusable/TableSkeleton';
import useErrorModal from '../hooks/useErrorModal';
import { AxiosError } from 'axios';
import TranscriptsTablePrintButton from './print/TranscriptsTablePrintButton';

interface TranscriptSelectionTableProps {
    categoryName: keyof typeof TRANSCRIPTS_CATEGORIES;
    isSideDrawerOpen: boolean;
    setTranscriptsError: React.Dispatch<React.SetStateAction<string>>;
    setTranscriptsUploaded: React.Dispatch<React.SetStateAction<boolean>>
}

const tableHeaderData: TableHeaderData<Transcript>[] = [
  { key: '', label: '' },
  { key: 'studentName', label: 'Student' },
  { key: 'gpa', label: 'GPA' },
  { key: 'credits', label: 'Credits' },
  { key: 'course_intensity', label: 'Course intensity'},
  { key: 'raceEth', label: 'Race/ethnicity' },
  { key: 'gender', label: 'Gender' },
  { key: 'disabilityStatus', label: 'Students with disabilities' },
  { key: 'englishLearner', label: 'English language learners' },
  { key: 'lowIncome', label: 'Low income' },
  { key: '', label: '' }
];
    
const renderSelectionLimitText = (selectedCount: number, selectionLimit: number) => {
  const limitExceeded = selectedCount > selectionLimit;
      
  return (
    <p className={limitExceeded ? 'selection-error-message' : ''}>
      {`${selectedCount}/${selectionLimit} selected`}
      {limitExceeded && ` - Select up to ${formatHelperTextCount(selectionLimit, 'student')}`}
    </p>
  );
};
    
const renderHeaderHelperText = (categoryName: string, selectionLimit: number) => {
    
  const selectionLimitText = <> Select <span className="bolded">{formatHelperTextCount(selectionLimit, 'student')}</span> </>;
      
  switch(categoryName) {
  case TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC:
    return (
      <> {selectionLimitText} from the highest achievers to represent the High flier category. </>
    );
  case TRANSCRIPTS_CATEGORIES.ON_THE_CUSP: 
    return (
      <> {selectionLimitText} based on their satisfaction of college eligibility criteria to represent the On the cusp category. </>
    );
  case TRANSCRIPTS_CATEGORIES.STRUGGLING: 
    return (
      <> {selectionLimitText} based on lack of college eligibility criteria to represent the Struggling category. </>
    );
  }
};

function TranscriptSelectionTable({categoryName, isSideDrawerOpen, setTranscriptsError, setTranscriptsUploaded}: TranscriptSelectionTableProps) {

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const { userDetails: { data: userDetails } } = useUserDetails();
  const { selectedTranscriptsSummaryResponse: {selectedTranscriptsSummary}, updateDetailsTranscripts, updateDetailsCategory, updateDetailsLinks} = useTranscriptSelectionOutletContext();
  const {lastNavigatedTranscriptSelectionPage} = useTranscriptSelectionPaginationData();
  const { transcriptSelectionSchool } = useSchools();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const [hasNextBatch, setHasNextBatch] = useState(false);
  const [page, setPage] = useState(lastNavigatedTranscriptSelectionPage.current[categoryName]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingTranscripts, setLoadingTranscripts] = useState(false);

  const { isPending, error, data } = useFetchData({
    fetchData: async (eoaId: string, access_token: string) => {

      setTranscriptsError('');
      setTranscriptsUploaded(true);
      
      const result = await getTranscriptSelectionSummary(eoaId, access_token, transcriptSelectionSchool, categoryName, page - 1);
      const linkHeader = result.headers.link;
      setTotalCount(Number(result.headers['x-total-count']));
      setHasNextBatch(result.data.has_next_batch);

      return {
        data: {
          is_finalized: result.data.is_finalized,
          selection_counts: {
            [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: result.data.selection_counts[TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC],
            [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: result.data.selection_counts[TRANSCRIPTS_CATEGORIES.ON_THE_CUSP],
            [TRANSCRIPTS_CATEGORIES.STRUGGLING]: result.data.selection_counts[TRANSCRIPTS_CATEGORIES.STRUGGLING]
          },
          transcripts: result.data.transcripts[categoryName],
          hasPreviousPage: checkForLink(linkHeader, 'prev'),
          hasNextPage: checkForLink(linkHeader, 'next')
        },
        
      };
    },
    errorMessage: `Error occurred while fetching ${categoryName.split('_').join(' ').toLowerCase()} transcripts data!`,
    options: {
      dependencies: [page],
      areDependenciesRequired: true
    },
    initialState: {
      is_finalized: false,
      selection_counts: {
        [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: 0,
        [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: 0,
        [TRANSCRIPTS_CATEGORIES.STRUGGLING]: 0,
      },
      transcripts: [],
      hasPreviousPage: false,
      hasNextPage: false
    }
  });

  const handleLoadMoreTranscripts = async () => {
    if (userDetails) {
      try {
        setLoadingTranscripts(true);
        const result = await getTranscriptSelectionSummary(userDetails.eoaId, userDetails.access_token, transcriptSelectionSchool, categoryName, 0, true);
        setTotalCount(Number(result.headers['x-total-count']));
        setHasNextBatch(result.data.has_next_batch);
        setLoadingTranscripts(false);
      } catch (error) {
        setLoadingTranscripts(false);
        handleOpenErrorModal('Error occurred while loading more transcripts!', error as AxiosError);
      }
    }
  };

  const updateCurrentPage = (newPage: number) => {
    setPage(newPage);
    lastNavigatedTranscriptSelectionPage.current = storeLastNavigatedPage(lastNavigatedTranscriptSelectionPage.current, categoryName, newPage);
  };

  const selectedCount = selectedTranscriptsSummary.selection_counts[categoryName];
  const pageSize = TRANSCRIPTS_PAGE_SIZES[categoryName];

  useEffect(() => {
    if (error) {
      setTranscriptsError(error.message);
    }
    
    if (!isPending && !data?.transcripts?.length) {
      setTranscriptsUploaded(false);
    }
  }, [error, isPending, data]);

  const handleOpenDetails = () => {
    updateDetailsTranscripts(data?.transcripts || []);
    updateDetailsCategory(categoryName);
    updateDetailsLinks({
      hasPreviousPage: data?.hasPreviousPage || false,
      hasNextPage: data?.hasNextPage || false
    });
  };

  return (
    <div className="transcript-selection-category-container" key={categoryName}>
      <div className="transcript-selection-category-top-section">
        <div className="transcript-selection-category-header">  
          <div className="title">
            <p>{TRANSCRIPTS_CATEGORIES_LABELS[categoryName as keyof typeof TRANSCRIPTS_CATEGORIES]}</p>
            <CustomTooltip 
              title="Test title"
              placement="top"
              arrow={true}>
              <img src={InfoIcon} alt="info icon"/>
            </CustomTooltip>
          </div>
          {renderHeaderHelperText(categoryName, TRANSCRIPTS_CATEGORIES_SELECTION_LIMITS[categoryName])}
        </div>
        <div className="transcript-selection-category-header-actions">
          {renderSelectionLimitText(selectedCount, TRANSCRIPTS_CATEGORIES_SELECTION_LIMITS[categoryName])}
          <TranscriptsTablePrintButton transcripts={data?.transcripts || []} />
        </div>
      </div>
      <CustomTable
        tableHeaderData={tableHeaderData}
        headerTooltip={isSmallScreen || isSideDrawerOpen}
        caption={
          <div className="table-caption">
            {hasNextBatch ?
              <div className="load-more">
                {!isPending && !loadingTranscripts ?
                  <>
                    <p>Don't see good transcripts?</p>
                    <CustomButton
                      variant="text-underlined"
                      buttonText="Load more transcripts"
                      buttonAction={handleLoadMoreTranscripts}
                    />
                  </>
                  :
                  <Skeleton variant="text" animation="wave" sx={{ width: '350px' }} />
                }
              </div>
              :
              null
            }
            { !data.is_finalized ?
              <>
                { !isPending && !loadingTranscripts ?
                  <CustomPagination
                    currentPage={page}
                    handlePageChange={updateCurrentPage}
                    pages={Math.ceil(totalCount / pageSize)}
                    totalItems={totalCount}
                    pageSize={pageSize}
                    classes={['in-transcript-selection-table']}
                  />
                  :
                  <Skeleton variant="text" animation="wave" sx={{ width: '200px', marginLeft: 'auto' }} />
                }
              </>
              :
              null
            }
          </div>
        }>
        {isPending || loadingTranscripts ? 
          <TableSkeleton rowsNumber={pageSize} colsNumber={tableHeaderData.length} /> 
          :
          data?.transcripts?.map((transcript: Transcript, index: number) => (
            <TranscriptSelectionTableRow
              key={`${index}-${transcript.id}`}
              row={transcript}
              rowIndex={index}
              transcripts={data?.transcripts || []}
              category={categoryName}
              updateDetailsTranscripts={handleOpenDetails}
            />
          ))
        }
      </CustomTable>
    </div>
  );
}

export default TranscriptSelectionTable;